import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import * as newAddressData from 'assets/json/new-address.json';
import { UserAddressService } from '@app/api/api';
import { currentUser, User } from '@app/core/auth';
import { AppState } from '@app/core/reducers';
import { ToastrService } from 'ngx-toastr';
import { UtilService } from '@app/services/util.service';

@Component({
  selector: 'meu-app-modal-update-address',
  templateUrl: './app-modal-update-address.component.html',
  styleUrls: ['./app-modal-update-address.component.scss']
})
export class AppModalUpdateAddressComponent implements OnInit {
  addressForm = new FormGroup({
    fullname: new FormControl('', [Validators.required]),
    company_name: new FormControl(''),
    province: new FormControl('', [Validators.required]),
    district: new FormControl(''),
    ward: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    is_company: new FormControl(false, [Validators.required]),
    is_default: new FormControl(false),
    user_id: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    note: new FormControl(''),
  });

  lstAddressJson: any = [];
  lstProvince: any = [];
  lstWard: any = [];
  filteredOptionsLstProvince: Observable<string[]>;
  filteredOptionsLstWard: Observable<string[]>;
  user$: Observable<User>;
  myInfo: any;
  hasFormErrors: boolean = false;
  addressData: any;
  addressId: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AppModalUpdateAddressComponent>,
    private store: Store<AppState>,
    private userAddressService: UserAddressService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.addressId = this.data.addressId;
    this.lstAddressJson = Object.values(JSON.parse(JSON.stringify(newAddressData.default)));
    this.lstProvince = this.lstAddressJson.map((x: any) => x.Name);
    this.user$ = this.store.pipe(select(currentUser));
    this.user$.subscribe((user) => {
      this.myInfo = user;
    });
    this.filteredOptionsLstProvince = this.addressForm.get('province').valueChanges.pipe(
      startWith(''),
      map((value) => this._filterProvince(value))
    );
    if (this.addressId) {
      this.getAddressByID();
    }
  }

  getAddressByID() {
    this.userAddressService.apiUserAddressesIdGet(this.addressId).subscribe((res) => {
      if (res.success) {
        this.addressData = res.data;
        this.addressForm.patchValue({
          fullname: res.data.fullname,
          company_name: res.data.company_name,
          province: res.data.province,
          district: res.data.district,
          ward: res.data.ward,
          phone: res.data.phone,
          address: res.data.address,
          is_company: res.data.is_company || false,
          is_default: res.data.is_default || false,
          user_id: res.data.user_id,
          country: res.data.country || 'Việt Nam',
          note: res.data.note
        });
        this.updateLstWard(true);
      }
    });
  }

  private _filterProvince(value: string): string[] {
    if (value != null) {
      const filterValue = UtilService.slugify(value.toLowerCase());
      return this.lstProvince.filter((option: any) =>
        UtilService.slugify(option.toLowerCase()).includes(filterValue)
      );
    }
    return this.lstProvince;
  }

  private _filterWard(value: string): string[] {
    if (value != null) {
      const filterValue = UtilService.slugify(value.toLowerCase());
      return this.lstWard
        .map((x: any) => x.FullName)
        .filter((option: any) => UtilService.slugify(option.toLowerCase()).includes(filterValue));
    }
    return this.lstWard.map((x: any) => x.FullName);
  }

  updateLstWard(keepWard: boolean = false) {
    if (this.addressForm.value.province != null && this.addressForm.value.province != '') {
      if (!keepWard) {
        this.addressForm.patchValue({ ward: '' });
      }
      let province = this.lstAddressJson.find((x: any) => x.Name == this.addressForm.value.province);
      this.lstWard = [];
      if (province != null && province.Wards) {
        this.lstWard = Object.values(JSON.parse(JSON.stringify(province.Wards)));
      }
      this.filteredOptionsLstWard = this.addressForm.get('ward').valueChanges.pipe(
        startWith(''),
        map((value) => this._filterWard(value))
      );
      this.addressForm.patchValue({ district: null});
    }
  }

  updateMyAddress() {
    this.addressForm.patchValue({
      district: null,
      user_id: this.addressForm.value.user_id || (this.myInfo ? this.myInfo.id : '')
    });
    
    // Clear company name if it's not a company address to keep clean request
    if (!this.addressForm.value.is_company) {
      this.addressForm.patchValue({
        company_name: ''
      });
    }

    const controls = this.addressForm.controls;
    if (this.addressForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    
    this.userAddressService.apiUserAddressesIdPut(this.addressId, this.addressForm.value).subscribe((res) => {
      if (res.success) {
        this.toastr.success('Cập nhật địa chỉ thành công');
        this.dialogRef.close({ event: true, data: res.data });
      }
    });
  }

  cancel() {
    this.dialogRef.close({ event: false });
  }
}
